import { EntityType } from "@/services/hooks/entities/useEntityType";
import { EntityData } from "@/services/api/types";
import { useCompanies } from "@/pages/companies/hooks";
import { useEntityMultiSelect } from "@/services/hooks/entities/useEntityMultiSelect";
import { multiSelectOption } from "@/components/controls/MultiSelect";
import { useCompanyHasPeople } from "./useCompanyHasPeople";
import { ActivityData } from "@/entities/activities/types";

interface UseActivityModalDataParams {
  isEditMode: boolean;
  editActivityData?: ActivityData | null;
  entityType: EntityType;
  entityData: EntityData | undefined;
  selectedParticipants: string[];
}

interface UseActivityModalDataReturn {
  activityData: ActivityData | undefined;
  relatedCompanyData: EntityData | undefined;
  isCompanySet: boolean;
  participantsOptions: multiSelectOption[];
  onParticipantsSearch: (search: string) => void;
  isLoadingParticipants: boolean;
  relatedPersonData: EntityData | null;
  effectiveCompanyId: string;
}

export const useActivityModalData = ({
  isEditMode,
  editActivityData,
  entityType,
  entityData,
  selectedParticipants
}: UseActivityModalDataParams): UseActivityModalDataReturn => {
  const activityData = isEditMode ? (editActivityData ?? undefined) : undefined;

  const relatedCompanyData =
    entityType === EntityType.COMPANY
      ? entityData
      : useCompanies([entityData?.companyId]).entities[0];

  const effectiveCompanyId = relatedCompanyData?.id || "";
  // Partners have no companyId field — the activity is associated via the
  // partner itself (backend infers partnerId from the entity_id), so a
  // company association is never required here.
  const isCompanySet =
    entityType === EntityType.PARTNER || !!relatedCompanyData?.id;

  const relatedPersonData: EntityData | null =
    entityType === EntityType.PERSON ? entityData || null : null;

  const {
    hasPeople: companyHasPeople,
    isLoading: isCheckingCompanyPeople,
    isError: isCompanyPeopleCheckError
  } = useCompanyHasPeople({
    companyId: effectiveCompanyId
  });

  const shouldFilterByCompany =
    Boolean(effectiveCompanyId) &&
    !isCompanyPeopleCheckError &&
    (companyHasPeople || isCheckingCompanyPeople);

  const {
    options: participantsOptions,
    onSearch: onParticipantsSearch,
    isLoading: isLoadingParticipants
  } = useEntityMultiSelect({
    entityType: EntityType.PERSON,
    selectedIds: selectedParticipants,
    filters: shouldFilterByCompany ? { companyId: effectiveCompanyId } : {}
  });

  return {
    activityData,
    relatedCompanyData,
    isCompanySet,
    participantsOptions: participantsOptions || [],
    onParticipantsSearch,
    isLoadingParticipants:
      isLoadingParticipants ||
      (isCheckingCompanyPeople && !isCompanyPeopleCheckError),
    relatedPersonData,
    effectiveCompanyId
  };
};
